import { format } from "date-fns";
import { Clock3, Grid2X2, Repeat2, Zap } from "lucide-react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { formatDuration } from "../../utils";
import { type SessionProcess, highestActivityPeriod } from "./sessionStory";
import type { AnalyticsModel } from "./utils";

type Props = {
  model: AnalyticsModel;
  groups: SessionProcess[];
};

export default function SessionHighlights({ model, groups }: Props) {
  const { t } = useTranslation();
  const activity = useMemo(() => highestActivityPeriod(model), [model]);
  const mostRepeated = [...groups].sort(
    (a, b) => b.runs.length - a.runs.length || b.totalMin - a.totalMin,
  )[0];
  const mostTime = groups[0];
  const topApp = model.appUsage[0];
  const durationAndShare = (minutes: number) =>
    `${formatDuration(minutes * 60)} ${t("sessionStory.share", {
      percent: model.totalActive > 0 ? Math.round((minutes / model.totalActive) * 100) : 0,
    })}`;

  return (
    <section
      aria-label={t("sessionStory.highlights")}
      className="rounded-xl border border-outline bg-surface p-5 @[1100px]:p-6"
    >
      <h2 className="mb-4 text-base font-semibold text-text">{t("sessionStory.highlights")}</h2>
      <div className="grid gap-3 @[600px]:grid-cols-2">
        <Highlight
          icon={Repeat2}
          tone="bg-emerald-500/10 text-emerald-700"
          label={t("sessionStory.mostRepeated")}
          title={mostRepeated?.name}
          value={
            mostRepeated ? t("sessionStory.instances", { count: mostRepeated.runs.length }) : "—"
          }
        />
        <Highlight
          icon={Clock3}
          tone="bg-violet-500/10 text-violet-700"
          label={t("sessionStory.mostTime")}
          title={mostTime?.name}
          value={mostTime ? durationAndShare(mostTime.totalMin) : "—"}
        />
        <Highlight
          icon={Grid2X2}
          tone="bg-blue-500/10 text-blue-700"
          label={t("sessionStory.topApp")}
          title={topApp?.name}
          value={topApp ? durationAndShare(topApp.totalMin) : "—"}
          note={!topApp ? t("sessionStory.noAppData") : undefined}
        />
        <Highlight
          icon={Zap}
          tone="bg-amber-500/10 text-amber-800"
          label={t("sessionStory.highestActivity")}
          title={
            activity
              ? `${format(activity.start, "h:mm a")}–${format(activity.end, "h:mm a")}`
              : undefined
          }
          value={activity ? t("sessionStory.changes", { count: activity.count }) : "—"}
        />
      </div>
    </section>
  );
}

function Highlight({
  icon: Icon,
  tone,
  label,
  title,
  value,
  note,
}: {
  icon: typeof Clock3;
  tone: string;
  label: string;
  title?: string;
  value: string;
  note?: string;
}) {
  return (
    <div
      className={`flex min-h-32 min-w-0 flex-col items-start justify-center rounded-xl p-4 text-left ${tone}`}
    >
      <div className="flex items-center gap-2">
        <Icon size={18} className="shrink-0" aria-hidden="true" />
        <h3 className="text-xs font-medium leading-4 text-text-mid">{label}</h3>
      </div>
      {title && (
        <p className="mt-2 break-words text-sm font-semibold leading-5 text-text">{title}</p>
      )}
      <p className="mt-2 text-sm font-semibold leading-5 tabular-nums">{value}</p>
      {note && <p className="mt-1 text-xs leading-4 text-text-dim">{note}</p>}
    </div>
  );
}
